import { useState, useEffect, useCallback } from "react";
import api, { fmtDate, errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const { hasPerm } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", active: true });
  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/users", { params: { page, limit: 15 } });
      setData(d);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.email || (!editing && !form.password)) {
      toast.error("Ad, e-posta ve şifre zorunludur"); return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success(editing ? "Kullanıcı güncellendi" : "Kullanıcı oluşturuldu");
      setOpen(false);
      load();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteRow.id}`);
      toast.success("Kullanıcı silindi");
      setDeleteRow(null);
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Kullanıcılar" subtitle="Sistem kullanıcılarını yönetin">
        {hasPerm("users", "create") && (
          <Button data-testid="user-add-button"
            onClick={() => { setEditing(null); setForm({ name: "", email: "", password: "", active: true }); setOpen(true); }}
            className="rounded-none bg-[#D4AF37] font-semibold text-[#0B1F3A] hover:bg-[#c6a230]">
            <Plus size={18} className="mr-1" /> Yeni Kullanıcı
          </Button>
        )}
      </PageHeader>

      <DataTable testId="users-table" columns={[
        { key: "name", label: "Ad Soyad", render: (r) => <span className="font-medium">{r.name}</span> },
        { key: "email", label: "E-posta" },
        {
          key: "role", label: "Rol",
          render: (r) => (
            <span className={`px-2 py-1 text-xs font-semibold ${r.role === "super_admin" ? "bg-[#D4AF37]/15 text-[#a3822a]" : "bg-gray-100 text-gray-600"}`}>
              {r.role === "super_admin" ? "Super Admin" : "Personel"}
            </span>
          ),
        },
        {
          key: "active", label: "Durum",
          render: (r) => (
            <span className={`px-2 py-1 text-xs font-semibold ${r.active !== false ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
              {r.active !== false ? "Aktif" : "Pasif"}
            </span>
          ),
        },
        { key: "created_at", label: "Kayıt Tarihi", render: (r) => fmtDate(r.created_at) },
      ]} data={data.items} loading={loading} page={page} pages={data.pages} total={data.total} onPage={setPage}
        actions={(row) => (
          <>
            {hasPerm("users", "edit") && (
              <button data-testid={`user-edit-${row.id}`}
                onClick={() => { setEditing(row); setForm({ name: row.name, email: row.email, password: "", active: row.active !== false }); setOpen(true); }}
                className="flex h-8 w-8 items-center justify-center border border-gray-200 text-[#0B1F3A] hover:border-[#D4AF37]">
                <Pencil size={14} />
              </button>
            )}
            {hasPerm("users", "delete") && row.role !== "super_admin" && (
              <button data-testid={`user-delete-${row.id}`} onClick={() => setDeleteRow(row)}
                className="flex h-8 w-8 items-center justify-center border border-gray-200 text-red-600 hover:border-red-400">
                <Trash2 size={14} />
              </button>
            )}
          </>
        )} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Ad Soyad *</Label>
              <Input data-testid="user-name-input" className="rounded-none" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">E-posta *</Label>
              <Input data-testid="user-email-input" type="email" className="rounded-none" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Şifre {editing ? "(değiştirmek için doldurun)" : "*"}
              </Label>
              <Input data-testid="user-password-input" type="password" className="rounded-none" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            {editing?.role !== "super_admin" && (
              <div className="flex items-center gap-3">
                <Switch data-testid="user-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label className="text-sm">Aktif</Label>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button data-testid="user-save-button" disabled={saving} onClick={save}
                className="rounded-none bg-[#0B1F3A] text-white hover:bg-[#132c4f]">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteRow)} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı silmek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>{deleteRow?.name} sisteme erişemeyecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="user-confirm-delete" onClick={doDelete} className="rounded-none bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
